import { useCallback, useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { SearchAutocompleteResponse, SearchParams, SearchType } from '../../types';
import { searchParamsBackwardsCompatibility } from '../utils/searchHistory';

type UseSearchPanelProps = {
  searchType: SearchType;
  showPanel: false | 'searchPanel' | 'searchDates' | 'materieeltypen' | 'searchAutocomplete';
  isWithinAdvancedSearch: boolean;
  suggestions: SearchAutocompleteResponse;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
};
const useSearchPanels = ({
  searchType: __,
  showPanel,
  isWithinAdvancedSearch,
  suggestions,
  selectedIndex,
  setSelectedIndex,
}: UseSearchPanelProps) => {
  const [_searchHistory, setSearchHistory] = useLocalStorage<SearchParams[]>('searchHistory', []);

  const searchHistory = useMemo(() => {
    return (
      isWithinAdvancedSearch
        ? _searchHistory.filter((historic) => !(isWithinAdvancedSearch && historic?.searchType === 'rollingStockType'))
        : _searchHistory
    ).map(searchParamsBackwardsCompatibility);
  }, [_searchHistory, isWithinAdvancedSearch]);

  const currentListSize = useMemo<number>(() => {
    if (showPanel === 'searchPanel') return searchHistory.length;
    if (showPanel === 'searchAutocomplete') return (suggestions.length ?? 0) + 1; // Plus 1 because the raw query also needs to be selectable
    return 0;
  }, [showPanel, searchHistory, suggestions]);

  const arrowDown = useCallback(() => {
    setSelectedIndex(
      (() => {
        if (currentListSize === 0 || showPanel === false) return 0;
        return (selectedIndex !== 0 && selectedIndex === currentListSize - 1) ||
          (selectedIndex === 0 && currentListSize === 1)
          ? selectedIndex
          : selectedIndex + 1;
      })(),
    );
  }, [currentListSize, showPanel, selectedIndex]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [showPanel]);

  const arrowUp = useCallback(
    () => setSelectedIndex(selectedIndex === 0 ? selectedIndex : selectedIndex - 1),
    [showPanel, selectedIndex, currentListSize],
  );

  const updateSearchHistory = (newSearchTerm: SearchParams) =>
    setSearchHistory((history) =>
      [newSearchTerm, ...history.filter((e) => JSON.stringify(e) !== JSON.stringify(newSearchTerm))]
        .map(searchParamsBackwardsCompatibility)
        .slice(0, 5),
    );

  return {
    arrowDown,
    arrowUp,
    selectedIndex,
    searchHistory,
    updateSearchHistory,
    setSelectedIndex,
  };
};
export default useSearchPanels;
